import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import logo from "@/assets/logo.png";

const quickLinks = [
  { name: "Home", path: "/" },
  { name: "Cabanas", path: "/cabanas" },
  { name: "Tubing", path: "/tubing" },
  { name: "Glamping", path: "/glamping" },
  { name: "Property Map", path: "/property-map" },
  { name: "FAQs", path: "/faqs" },
  { name: "Contact Us", path: "/contact" },
];

const policyLinks = [
  { name: "Rental Agreement & Waiver", path: "/waivers" },
  { name: "Privacy Policy", path: "/privacy" },
  { name: "Cancellation Policy", path: "/cancellation" },
];

export function Footer() {
  return (
    <footer className="bg-charcoal text-cream">
      {/* Main Footer */}
      <div className="container-narrow mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <img 
                src={logo} 
                alt="Son's Guadalupe" 
                width={217}
                height={64}
                loading="lazy"
                className="h-16 w-auto"
              />
            </div>
            <p className="text-cream/70 text-sm leading-relaxed mb-6">
              Your private riverside basecamp on the Guadalupe — where comfort, control, and family fun replace chaos, crowds, and stress.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-cream/10 flex items-center justify-center hover:bg-cream/20 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-cream/10 flex items-center justify-center hover:bg-cream/20 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="font-heading font-bold text-lg mb-4">Quick Links</h2>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-cream/70 hover:text-cream transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies */}
          <div>
            <h2 className="font-heading font-bold text-lg mb-4">Policies</h2>
            <ul className="space-y-2">
              {policyLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-cream/70 hover:text-cream transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h2 className="font-heading font-bold text-lg mb-4">Contact Us</h2>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:877-577-7667"
                  className="flex items-start gap-3 text-cream/70 hover:text-cream transition-colors text-sm"
                >
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>877-577-7667<br /><span className="text-xs">9am-7pm Daily</span></span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@sonsgetaways.com"
                  className="flex items-center gap-3 text-cream/70 hover:text-cream transition-colors text-sm"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span>info@sonsgetaways.com</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=7308+River+Road,+New+Braunfels,+TX+78132"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-cream/70 hover:text-cream transition-colors text-sm"
                >
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>7308 River Road<br />New Braunfels, TX 78132</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* SEO / Explore */}
      <div className="border-t border-cream/10 bg-charcoal/60">
        <div className="container-narrow mx-auto px-4 md:px-8 py-8">
          <h2 className="font-heading font-bold text-base mb-4 text-cream/90">Explore the Guadalupe River</h2>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/tubing" className="text-cream/70 hover:text-cream transition-colors">Tubing the Guadalupe River</Link>
            <Link to="/cabanas" className="text-cream/70 hover:text-cream transition-colors">Cabana Rentals Near New Braunfels</Link>
            <Link to="/glamping" className="text-cream/70 hover:text-cream transition-colors">Glamping Near New Braunfels</Link>
            <Link to="/glamping" className="text-cream/70 hover:text-cream transition-colors">Tent Camping on River Road</Link>
            <Link to="/why-sons" className="text-cream/70 hover:text-cream transition-colors">Family Getaways Near New Braunfels</Link>
            <Link to="/property-map" className="text-cream/70 hover:text-cream transition-colors">Property Map</Link>
            <Link to="/blog" className="text-cream/70 hover:text-cream transition-colors">Blog</Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-cream/10">
        <div className="container-narrow mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-cream/50">
            <p>© {new Date().getFullYear()} Son's Guadalupe. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2">
                <span className="text-yellow-400">★</span> 4.1 Google Rating
              </span>
              <span>|</span>
              <span>Family-Owned</span>
              <span>|</span>
              <span>10+ Years</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
